import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchCategories } from '../lib/api';
import { createListing } from '../lib/api';
import { seedCategories } from '../data/seed';
import { PUBLIC_PLANS, annualTotal, type BillingPeriod, type PlanKey } from '../lib/pricing';
import type { Category } from '../types';

const REGIONS = [
  'Auckland', 'Bay of Plenty', 'Canterbury', 'Coromandel', 'Far North',
  'Gisborne', "Hawke's Bay", 'Manawatū-Whanganui', 'Marlborough', 'Nelson',
  'Northland', 'Otago', 'Southland', 'Taranaki', 'Waikato', 'Wellington',
  'West Coast', 'Whangarei'
];

export default function ListBusiness() {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>(seedCategories);
  const [submitted, setSubmitted] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  const initialPlan = (searchParams.get('plan') || 'free') as PlanKey;
  const [plan, setPlan] = useState<PlanKey>(PUBLIC_PLANS[initialPlan] ? initialPlan : 'free');
  const [billing, setBilling] = useState<BillingPeriod>(searchParams.get('billing') === 'annual' ? 'annual' : 'monthly');
  const [form, setForm] = useState({
    name: '', category: '', region: '', phone: '', email: '',
    website: '', description: '', services: '', image: ''
  });

  useEffect(() => {
    fetchCategories().then(setCategories);
  }, []);

  const selectedPlan = PUBLIC_PLANS[plan];
  const isPaid = plan !== 'free';
  const total = billing === 'annual'
    ? annualTotal(selectedPlan.monthlyPrice, selectedPlan.annualMonths)
    : selectedPlan.monthlyPrice;
  const priceLabel = isPaid
    ? billing === 'annual'
      ? `$${(annualTotal(selectedPlan.monthlyPrice, selectedPlan.annualMonths) / 12).toFixed(2)}/mo equivalent - $${total}/yr`
      : `$${selectedPlan.monthlyPrice}/month`
    : 'Free for 3 months';

  const trackEvent = (name: string, properties: Record<string, string> = {}) => {
    fetch('/.netlify/functions/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, properties }),
      keepalive: true,
    }).catch(() => undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.category || !form.region || !form.phone || !form.email || !form.description) {
      setError('Please fill in all required fields');
      return;
    }

    const cat = categories.find(c => c.id === form.category);
    trackEvent('listing_form_submitted', { plan, billing_period: billing });
    const result = await createListing({
      title: form.name,
      category_id: form.category,
      category_name: cat?.name,
      region_name: form.region,
      phone: form.phone,
      email: form.email,
      website: form.website,
      description: form.description,
      services: form.services.split(',').map(s => s.trim()).filter(Boolean),
      image_url: form.image,
      rating: 0,
      reviews_count: 0,
      badge: 'new',
    });

    if (result.success) {
      if (!isPaid) {
        trackEvent('listing_created_free', { plan: 'free' });
        setSubmitted(true);
        return;
      }

      setRedirecting(true);
      trackEvent('checkout_started', { plan, billing_period: billing });
      try {
        const checkoutResponse = await fetch('/.netlify/functions/checkout-new', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ listing_id: result.id, plan, billing_period: billing }),
        });
        const checkout = await checkoutResponse.json();
        if (checkout.checkout_url) {
          window.location.href = checkout.checkout_url;
        } else {
          setError(checkout.error || 'Your listing was created, but payment could not be started. Please contact us.');
          setRedirecting(false);
        }
      } catch {
        setError('Your listing was created, but payment could not be started. Please contact us.');
        setRedirecting(false);
      }
    } else {
      setError(result.error || 'Something went wrong');
    }
  };

  return (
    <div className="max-w-[800px] mx-auto px-4 md:px-8 py-6 md:py-12 pb-24">
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/" className="text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <span>List your business</span>
      </div>
      <h1 className="font-display text-2xl md:text-[1.8rem] font-bold mb-2">List your marine business</h1>
      <p className="text-text-secondary mb-6">Get found by thousands of boaties, sailors and maritime professionals across New Zealand. Choose Free, Pro or Premium below.</p>

      {submitted ? (
        <div className="bg-card-light dark:bg-card border border-green rounded-lg p-6 text-center mt-6">
          <h3 className="text-green text-lg mb-2">✓ Listing submitted!</h3>
          <p className="text-text-secondary">Your business has been added to MarineLink. It will appear in search results immediately.</p>
          <Link to="/browse" className="btn btn-ghost mt-4">View all listings</Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <div className="bg-card-light dark:bg-card border border-red rounded-lg p-4 mb-4 text-sm text-red">{error}</div>}

          <div className="bg-card-light dark:bg-card border border-[rgba(79,179,217,0.18)] rounded-lg p-5 mb-6">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h2 className="font-display text-lg font-bold">Choose your plan</h2>
                <p className="text-text-secondary text-sm mt-1">You can upgrade later. Annual plans include 2 months free.</p>
              </div>
              <span className="text-accent font-semibold text-sm">{priceLabel}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(Object.keys(PUBLIC_PLANS) as PlanKey[]).map((key) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => { setPlan(key); trackEvent('listing_plan_selected', { plan: key, billing_period: billing }); }}
                  className={`text-left rounded-md border px-4 py-3 transition-colors ${plan === key ? 'border-accent bg-accent-glow' : 'border-[rgba(79,179,217,0.14)] hover:border-accent'}`}
                >
                  <span className="block font-semibold text-sm">{PUBLIC_PLANS[key].name}</span>
                  <span className="block text-xs text-text-muted mt-1">
                    {key === 'free' ? 'Free forever' : `$${PUBLIC_PLANS[key].monthlyPrice}/month`}
                  </span>
                </button>
              ))}
            </div>
            {isPaid && (
              <div className="flex items-center gap-2 mt-4 text-sm">
                <span className="text-text-secondary">Billing:</span>
                <button type="button" onClick={() => setBilling('monthly')} className={`px-3 py-1 rounded-md ${billing === 'monthly' ? 'bg-accent text-white dark:text-bg' : 'text-text-secondary border border-[rgba(79,179,217,0.2)]'}`}>Monthly</button>
                <button type="button" onClick={() => setBilling('annual')} className={`px-3 py-1 rounded-md ${billing === 'annual' ? 'bg-accent text-white dark:text-bg' : 'text-text-secondary border border-[rgba(79,179,217,0.2)]'}`}>Annual - 2 months free</button>
              </div>
            )}
          </div>

          <div className="mb-5">
            <label className="block text-xs font-semibold text-text-secondary mb-2">Business name <span className="text-red">*</span></label>
            <input className="form-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Hauraki Gulf Sailing Charters" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-5">
              <label className="block text-xs font-semibold text-text-secondary mb-2">Category <span className="text-red">*</span></label>
              <select className="form-select" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="">Select a category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-text-secondary mb-2">Region <span className="text-red">*</span></label>
              <select className="form-select" required value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
                <option value="">Select a region</option>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-5">
              <label className="block text-xs font-semibold text-text-secondary mb-2">Phone <span className="text-red">*</span></label>
              <input className="form-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+64 ..." />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-text-secondary mb-2">Email <span className="text-red">*</span></label>
              <input className="form-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@yourbusiness.co.nz" />
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-xs font-semibold text-text-secondary mb-2">Website</label>
            <input className="form-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." />
          </div>

          <div className="mb-5">
            <label className="block text-xs font-semibold text-text-secondary mb-2">Description <span className="text-red">*</span></label>
            <textarea className="form-textarea" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell customers about your services, experience and what makes you different..." />
          </div>

          <div className="mb-5">
            <label className="block text-xs font-semibold text-text-secondary mb-2">Services offered</label>
            <input className="form-input" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} placeholder="Comma-separated, e.g. Charter hire, Skippered charters, Fishing trips" />
            <div className="text-xs text-text-muted mt-2">Separate each service with a comma.</div>
          </div>

          <div className="mb-5">
            <label className="block text-xs font-semibold text-text-secondary mb-2">Image URL</label>
            <input className="form-input" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://... (optional, we'll use a placeholder if not provided)" />
          </div>

          <button type="submit" disabled={redirecting} className="btn btn-primary btn-lg btn-block">
            {redirecting ? 'Opening secure checkout...' : isPaid ? `Continue to payment - ${priceLabel}` : 'Submit free listing'}
          </button>
        </form>
      )}
    </div>
  );
}
